import { Theme } from 'foundations-theming';
import { LinkProps } from './Link';

type UseProp = NonNullable<LinkProps['use']>;
type ColorToken = NonNullable<keyof Theme['color']>;

export type InteractiveStateTokenMap = {
  idle: ColorToken;
  hover: ColorToken;
  active: ColorToken;
  disabled: ColorToken;
};

/**
 * Generates the color tokens for the given link use.
 * The tokens are provided for each interactive state.
 * @param variant - The link use value.
 * @returns The color tokens for the given use.
 */
export const getColorTokensForUse = (
  use: UseProp,
  isControl?: boolean
): InteractiveStateTokenMap => {
  switch (use) {
    case 'blue':
    case 'dark':
    case 'light':
      if (isControl) {
        return {
          idle: 'specialty-text-interactive-alt-default',
          hover: 'specialty-text-interactive-alt-hover',
          active: 'specialty-text-interactive-alt-pressed',
          disabled: 'text-interactive-default-disabled',
        };
      }
      return {
        idle: 'text-interactive-default',
        hover: 'text-interactive-default-hover',
        active: 'text-interactive-default-pressed',
        disabled: 'text-core-disabled',
      };
    case 'mention':
    case 'on-bright':
      if (isControl) {
        return {
          idle: 'specialty-text-interactive-alt-default',
          hover: 'specialty-text-interactive-alt-hover',
          active: 'specialty-text-interactive-alt-pressed',
          disabled: 'text-interactive-default-disabled',
        };
      }
      return {
        idle: 'specialty-text-core-alt-default',
        hover: 'text-interactive-default-hover',
        active: 'text-interactive-default-pressed',
        disabled: 'text-core-disabled',
      };
    case 'danger':
      return {
        idle: 'text-alert-default',
        hover: 'text-alert-hover',
        active: 'text-alert-pressed',
        disabled: 'text-core-disabled',
      };
    case 'on-dark':
      return {
        idle: 'specialty-link-on-fill-alt-default',
        hover: 'specialty-link-on-fill-alt-hover',
        active: 'specialty-link-on-fill-alt-pressed',
        disabled: 'specialty-link-on-fill-alt-disabled',
      };
    case 'unstyled':
      return {
        idle: 'text-core-default',
        hover: 'text-core-default',
        active: 'text-core-default',
        disabled: 'text-core-disabled',
      };

    case 'on-fill':
      return {
        idle: 'specialty-link-on-fill-default',
        hover: 'specialty-link-on-fill-hover',
        active: 'specialty-link-on-fill-pressed',
        disabled: 'specialty-link-on-fill-disabled',
      };

    default: {
      const __exhaustiveCheck: never = use;

      throw new Error(`Unhandled \`use\` argument: ${use}`);
    }
  }
};
